import { forwardRef, useState, useMemo } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  Badge,
  ActionIcon,
  Menu,
  Tooltip,
  Paper,
  Select,
  Modal,
} from '@mantine/core';
import {
  IconChevronLeft,
  IconChevronRight,
  IconPlus,
  IconDots,
  IconEdit,
  IconTrash,
  IconCalendar,
} from '@tabler/icons-react';
import { Button, Card } from '../../ui';
import { FadeIn, HoverScale } from '../../animations';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';
import { pickTextColorForBackground } from '../../../utils/color';

export interface CalendarEvent {
  id: string | number;
  title: string;
  start: Date;
  end: Date;
  allDay?: boolean;
  color?: string;
  description?: string;
  location?: string;
  attendees?: { id: string; name: string; avatar?: string }[];
  category?: string;
  recurring?: boolean;
  metadata?: Record<string, unknown>;
}

export interface EventCalendarProps {
  events: CalendarEvent[];
  view?: 'month' | 'week' | 'day' | 'agenda';
  onViewChange?: (view: 'month' | 'week' | 'day' | 'agenda') => void;
  selectedDate?: Date;
  onDateChange?: (date: Date) => void;
  onEventClick?: (event: CalendarEvent) => void;
  onEventEdit?: (event: CalendarEvent) => void;
  onEventDelete?: (event: CalendarEvent) => void;
  onAddEvent?: (date: Date) => void;
  onDateSelect?: (date: Date) => void;
  categories?: { value: string; label: string; color: string }[];
  loading?: boolean;
  className?: string;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export const EventCalendar = forwardRef<HTMLDivElement, EventCalendarProps>(
  (
    {
      events,
      view = 'month',
      onViewChange,
      selectedDate = new Date(),
      onDateChange,
      onEventClick,
      onEventEdit,
      onEventDelete,
      onAddEvent,
      onDateSelect,
      categories = [],
      loading = false,
      className,
    },
    ref
  ) => {
    const [currentDate, setCurrentDate] = useState(selectedDate);
    // When a day has more events than fit in its month-grid cell, the user
    // opens this modal to see (and click through to) every event for that day.
    const [dayDetail, setDayDetail] = useState<Date | null>(null);
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const handlePrevious = () => {
      const newDate = new Date(currentDate);
      if (view === 'month') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else if (view === 'week') {
        newDate.setDate(newDate.getDate() - 7);
      } else {
        newDate.setDate(newDate.getDate() - 1);
      }
      setCurrentDate(newDate);
      onDateChange?.(newDate);
    };

    const handleNext = () => {
      const newDate = new Date(currentDate);
      if (view === 'month') {
        newDate.setMonth(newDate.getMonth() + 1);
      } else if (view === 'week') {
        newDate.setDate(newDate.getDate() + 7);
      } else {
        newDate.setDate(newDate.getDate() + 1);
      }
      setCurrentDate(newDate);
      onDateChange?.(newDate);
    };

    const handleToday = () => {
      const today = new Date();
      setCurrentDate(today);
      onDateChange?.(today);
    };

    const getMonthDays = useMemo(() => {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const daysInMonth = lastDay.getDate();
      const startingDay = firstDay.getDay();

      const days: (Date | null)[] = [];

      // Add empty slots for days before the first day of the month
      for (let i = 0; i < startingDay; i++) {
        days.push(null);
      }

      // Add days of the month
      for (let i = 1; i <= daysInMonth; i++) {
        days.push(new Date(year, month, i));
      }

      return days;
    }, [currentDate]);

    const getEventsForDate = (date: Date | null): CalendarEvent[] => {
      if (!date) return [];
      return events.filter((event) => {
        const eventDate = new Date(event.start);
        return (
          eventDate.getDate() === date.getDate() &&
          eventDate.getMonth() === date.getMonth() &&
          eventDate.getFullYear() === date.getFullYear()
        );
      });
    };

    const isToday = (date: Date | null): boolean => {
      if (!date) return false;
      const today = new Date();
      return (
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()
      );
    };

    const formatTime = (date: Date): string => {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const renderMonthView = () => (
      <Box>
        {/* Day headers */}
        <Group gap={0} mb="xs">
          {DAYS.map((day) => (
            <Box key={day} style={{ flex: 1, textAlign: 'center' }}>
              <Text size="sm" fw={500} c="dimmed" style={accessibilityStyles}>
                {day}
              </Text>
            </Box>
          ))}
        </Group>

        {/* Calendar grid */}
        <Box
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 1,
            backgroundColor: 'light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))',
            border: '1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))',
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          {getMonthDays.map((date, index) => {
            const dayEvents = getEventsForDate(date);
            const isCurrentDay = isToday(date);

            return (
              <Box
                key={index}
                style={{
                  minHeight: 100,
                  backgroundColor: date
                    ? 'var(--mantine-color-body)'
                    : 'light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-7))',
                  padding: 8,
                  cursor: date ? 'pointer' : 'default',
                }}
                onClick={() => {
                  if (!date) return;
                  // Preserve the date-select hook for consumers that use it
                  // (e.g. "add event on this day"); otherwise a click on a
                  // populated cell opens the day-detail modal.
                  if (onDateSelect) {
                    onDateSelect(date);
                  } else if (dayEvents.length > 0) {
                    setDayDetail(date);
                  }
                }}
              >
                {date && (
                  <Stack gap={4}>
                    <Group justify="space-between">
                      <Text
                        size="sm"
                        fw={isCurrentDay ? 700 : 400}
                        c={isCurrentDay ? 'blue' : undefined}
                        style={{
                          width: 24,
                          height: 24,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          backgroundColor: isCurrentDay
                            ? 'light-dark(var(--mantine-color-blue-1), var(--mantine-color-blue-9))'
                            : undefined,
                        }}
                      >
                        {date.getDate()}
                      </Text>
                      {onAddEvent && (
                        <ActionIcon
                          size="xs"
                          variant="subtle"
                          onClick={(e) => {
                            e.stopPropagation();
                            onAddEvent(date);
                          }}
                        >
                          <IconPlus size={12} />
                        </ActionIcon>
                      )}
                    </Group>
                    {dayEvents.slice(0, 3).map((event) => {
                      const bg = event.color || 'var(--mantine-color-blue-1)';
                      const fg = event.color ? pickTextColorForBackground(event.color) : undefined;
                      return (
                        <HoverScale key={event.id} scale={1.02}>
                          <Paper
                            p={4}
                            style={{
                              backgroundColor: bg,
                              cursor: 'pointer',
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onEventClick?.(event);
                            }}
                          >
                            <Text size="xs" lineClamp={1} style={{ color: fg }}>
                              {event.allDay ? event.title : `${formatTime(event.start)} ${event.title}`}
                            </Text>
                          </Paper>
                        </HoverScale>
                      );
                    })}
                    {dayEvents.length > 3 && (
                      <Text
                        size="xs"
                        c="blue"
                        fw={500}
                        style={{ ...accessibilityStyles, cursor: 'pointer' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDayDetail(date);
                        }}
                      >
                        +{dayEvents.length - 3} more
                      </Text>
                    )}
                  </Stack>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    );

    /**
     * Day view: events on the currently-focused day. We use the agenda card
     * layout for each event so users get a consistent visual no matter the view.
     */
    const renderDayView = () => {
      const dayEvents = getEventsForDate(currentDate);
      const dayLabel = currentDate.toLocaleDateString(undefined, {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      });

      return (
        <Stack gap="md">
          <Text size="sm" fw={600} style={accessibilityStyles}>{dayLabel}</Text>
          {dayEvents.length === 0 ? (
            <Paper p="md" withBorder style={{ textAlign: 'center' }}>
              <Text c="dimmed" size="sm">Nothing scheduled for this day</Text>
            </Paper>
          ) : (
            <Stack gap="xs">
              {dayEvents.map((event) => (
                <Paper
                  key={event.id}
                  p="sm"
                  withBorder
                  style={{ cursor: 'pointer' }}
                  onClick={() => onEventClick?.(event)}
                >
                  <Group justify="space-between">
                    <Group gap="sm">
                      <Box style={{ width: 4, height: 40, backgroundColor: event.color || 'var(--mantine-color-blue-5)', borderRadius: 2 }} />
                      <Box>
                        <Text fw={500} style={accessibilityStyles}>{event.title}</Text>
                        <Text size="sm" c="dimmed" style={accessibilityStyles}>
                          {event.allDay ? 'All day' : `${formatTime(event.start)} - ${formatTime(event.end)}`}
                        </Text>
                        {event.location && (
                          <Text size="xs" c="dimmed" style={accessibilityStyles}>{event.location}</Text>
                        )}
                      </Box>
                    </Group>
                  </Group>
                </Paper>
              ))}
            </Stack>
          )}
        </Stack>
      );
    };

    /**
     * Week view: 7 columns, one per day of the week containing currentDate.
     * Each column shows the day name + a stacked list of that day's events.
     * Keeps the renderer simple — no hour grid; that's only needed when
     * sub-hour overlap matters, which task due-dates don't.
     */
    const renderWeekView = () => {
      const start = new Date(currentDate);
      start.setDate(currentDate.getDate() - currentDate.getDay()); // Sunday
      const days: Date[] = [];
      for (let i = 0; i < 7; i++) {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        days.push(d);
      }

      return (
        <Box>
          <Group gap={0} mb="xs" grow>
            {days.map((d) => (
              <Box key={d.toDateString()} style={{ textAlign: 'center', padding: '8px 4px' }}>
                <Text size="xs" c="dimmed" style={accessibilityStyles}>
                  {DAYS[d.getDay()]}
                </Text>
                <Text size="sm" fw={isToday(d) ? 700 : 500} style={accessibilityStyles}>
                  {d.getDate()}
                </Text>
              </Box>
            ))}
          </Group>
          <Group gap={4} align="flex-start" grow>
            {days.map((d) => {
              const dayEvents = getEventsForDate(d);
              return (
                <Box
                  key={d.toDateString()}
                  style={{
                    minHeight: 240,
                    padding: 6,
                    borderRadius: 6,
                    border: isToday(d) ? '2px solid var(--mantine-color-blue-5)' : '1px solid var(--mantine-color-default-border)',
                  }}
                >
                  <Stack gap={4}>
                    {dayEvents.length === 0 ? (
                      <Text size="xs" c="dimmed" ta="center" style={accessibilityStyles}>—</Text>
                    ) : (
                      dayEvents.map((event) => (
                        <Box
                          key={event.id}
                          onClick={() => onEventClick?.(event)}
                          style={{
                            cursor: 'pointer',
                            padding: '4px 6px',
                            borderRadius: 4,
                            backgroundColor: event.color || 'var(--mantine-color-blue-5)',
                            color: 'white',
                            fontSize: 11,
                          }}
                        >
                          <Text size="xs" lineClamp={2} style={{ color: 'white', ...accessibilityStyles }}>
                            {event.allDay ? '' : `${formatTime(event.start)} · `}
                            {event.title}
                          </Text>
                        </Box>
                      ))
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Group>
        </Box>
      );
    };

    const renderAgendaView = () => {
      const sortedEvents = [...events].sort(
        (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
      );

      const groupedEvents: Record<string, CalendarEvent[]> = {};
      sortedEvents.forEach((event) => {
        const dateKey = new Date(event.start).toDateString();
        if (!groupedEvents[dateKey]) {
          groupedEvents[dateKey] = [];
        }
        groupedEvents[dateKey].push(event);
      });

      return (
        <Stack gap="md">
          {Object.entries(groupedEvents).map(([dateKey, dayEvents]) => (
            <Box key={dateKey}>
              <Text size="sm" fw={600} mb="xs" style={accessibilityStyles}>
                {new Date(dateKey).toLocaleDateString(undefined, {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </Text>
              <Stack gap="xs">
                {dayEvents.map((event) => (
                  <Paper
                    key={event.id}
                    p="sm"
                    withBorder
                    style={{ cursor: 'pointer' }}
                    onClick={() => onEventClick?.(event)}
                  >
                    <Group justify="space-between">
                      <Group gap="sm">
                        <Box
                          style={{
                            width: 4,
                            height: 40,
                            backgroundColor: event.color || 'var(--mantine-color-blue-5)',
                            borderRadius: 2,
                          }}
                        />
                        <Box>
                          <Text fw={500} style={accessibilityStyles}>{event.title}</Text>
                          <Text size="sm" c="dimmed" style={accessibilityStyles}>
                            {event.allDay
                              ? 'All day'
                              : `${formatTime(event.start)} - ${formatTime(event.end)}`}
                          </Text>
                          {event.location && (
                            <Text size="xs" c="dimmed" style={accessibilityStyles}>{event.location}</Text>
                          )}
                        </Box>
                      </Group>
                      {(onEventEdit || onEventDelete) && (
                        <Menu position="bottom-end">
                          <Menu.Target>
                            <ActionIcon variant="subtle" onClick={(e) => e.stopPropagation()}>
                              <IconDots size={16} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            {onEventEdit && (
                              <Menu.Item
                                leftSection={<IconEdit size={14} />}
                                onClick={() => onEventEdit(event)}
                              >
                                Edit
                              </Menu.Item>
                            )}
                            {onEventDelete && (
                              <Menu.Item
                                leftSection={<IconTrash size={14} />}
                                color="red"
                                onClick={() => onEventDelete(event)}
                              >
                                Delete
                              </Menu.Item>
                            )}
                          </Menu.Dropdown>
                        </Menu>
                      )}
                    </Group>
                  </Paper>
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      );
    };

    return (
      <FadeIn>
        <Card ref={ref} className={className}>
          <Stack gap="md">
            {/* Header */}
            <Group justify="space-between">
              <Group gap="sm">
                <ActionIcon variant="subtle" onClick={handlePrevious}>
                  <IconChevronLeft size={18} />
                </ActionIcon>
                <ActionIcon variant="subtle" onClick={handleNext}>
                  <IconChevronRight size={18} />
                </ActionIcon>
                <Text size="lg" fw={600} style={accessibilityStyles}>
                  {view === 'month'
                    ? `${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`
                    : currentDate.toLocaleDateString(undefined, {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                </Text>
              </Group>
              <Group gap="sm">
                <Button variant="subtle" size="xs" onClick={handleToday}>
                  Today
                </Button>
                {onViewChange && (
                  <Select
                    value={view}
                    onChange={(value) => onViewChange(value as 'month' | 'week' | 'day' | 'agenda')}
                    data={[
                      { value: 'month', label: 'Month' },
                      { value: 'week', label: 'Week' },
                      { value: 'day', label: 'Day' },
                      { value: 'agenda', label: 'Agenda' },
                    ]}
                    size="xs"
                    style={{ width: 100 }}
                  />
                )}
                {onAddEvent && (
                  <Button
                    size="xs"
                    leftSection={<IconPlus size={14} />}
                    onClick={() => onAddEvent(currentDate)}
                  >
                    Add Event
                  </Button>
                )}
              </Group>
            </Group>

            {/* Category legend */}
            {categories.length > 0 && (
              <Group gap="md">
                {categories.map((category) => (
                  <Group key={category.value} gap={4}>
                    <Box
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: 3,
                        backgroundColor: category.color,
                      }}
                    />
                    <Text size="xs" style={accessibilityStyles}>{category.label}</Text>
                  </Group>
                ))}
              </Group>
            )}

            {/* Calendar view */}
            {view === 'month' && renderMonthView()}
            {view === 'week' && renderWeekView()}
            {view === 'day' && renderDayView()}
            {view === 'agenda' && renderAgendaView()}
          </Stack>

          {/* Day-detail modal — every event for a busy day, each clickable. */}
          <Modal
            opened={!!dayDetail}
            onClose={() => setDayDetail(null)}
            title={
              dayDetail
                ? dayDetail.toLocaleDateString(undefined, {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })
                : ''
            }
            size="md"
            centered
          >
            <Stack gap="xs">
              {getEventsForDate(dayDetail).length === 0 ? (
                <Text c="dimmed" size="sm" style={accessibilityStyles}>
                  Nothing scheduled for this day.
                </Text>
              ) : (
                getEventsForDate(dayDetail).map((event) => (
                  <Paper
                    key={event.id}
                    p="sm"
                    withBorder
                    style={{ cursor: onEventClick ? 'pointer' : 'default' }}
                    onClick={() => {
                      if (!onEventClick) return;
                      onEventClick(event);
                      setDayDetail(null);
                    }}
                  >
                    <Group gap="sm" wrap="nowrap" align="flex-start">
                      <Box
                        style={{
                          width: 4,
                          alignSelf: 'stretch',
                          minHeight: 32,
                          backgroundColor: event.color || 'var(--mantine-color-blue-5)',
                          borderRadius: 2,
                        }}
                      />
                      <Box style={{ minWidth: 0 }}>
                        <Text fw={500} size="sm" style={accessibilityStyles}>
                          {event.title}
                        </Text>
                        <Text size="xs" c="dimmed" style={accessibilityStyles}>
                          {event.allDay
                            ? 'All day'
                            : `${formatTime(event.start)} - ${formatTime(event.end)}`}
                        </Text>
                        {event.location && (
                          <Text size="xs" c="dimmed" style={accessibilityStyles}>
                            {event.location}
                          </Text>
                        )}
                      </Box>
                    </Group>
                  </Paper>
                ))
              )}
            </Stack>
          </Modal>
        </Card>
      </FadeIn>
    );
  }
);

EventCalendar.displayName = 'EventCalendar';

export default EventCalendar;
